import { Helmet } from 'react-helmet-async';
import react, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Grid,
  Button,
  Container,
  Stack,
  Typography,
  Card,
  CardContent,
  CardHeader,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  CircularProgress,
  Radio,
  Switch,
} from '@mui/material';
// @mui
// components
import useAuth from '../hooks/useAuth';
import { updateSettings } from '../services/userService';
import { prefersLegacyCounts } from '../utils/particleView';
import { infoNotification, errorNotification } from '../services/notificationService';
import SubscriptionCard from '../components/subscription/SubscriptionCard';

export default function AccountPage() {
  const navigate = useNavigate();
  const { user, updateUser, refreshUser, logout } = useAuth();
  const [displayTemperatureAsFahrenheit, setDisplayTemperatureAsFahrenheit] = useState(
    user?.settings?.playback?.displayTemperatureAsFahrenheit || false
  );
  const [displayParticlesAsLegacyCounts, setDisplayParticlesAsLegacyCounts] = useState(
    prefersLegacyCounts(user)
  );

  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadUserData = async () => {
      try {
        setIsLoading(true);
        const userData = await refreshUser();
        setDisplayTemperatureAsFahrenheit(userData?.settings?.playback?.displayTemperatureAsFahrenheit || false);
        setDisplayParticlesAsLegacyCounts(prefersLegacyCounts(userData));
      } catch (error) {
        console.error('Error loading user data:', error);
        errorNotification(error.message || 'Failed to load user data');
      } finally {
        setIsLoading(false);
      }
    };

    if (user) {
      loadUserData();
    } else {
      setIsLoading(false);
    }
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  // Both playback flags stage onto the same in-memory user; Save is what
  // persists them. Patching rather than rebuilding the object keeps a new flag
  // from quietly dropping one that already exists.
  function stagePlayback(patch) {
    if (!user) return;
    updateUser({
      ...user,
      settings: {
        ...user.settings,
        playback: {
          ...user.settings?.playback,
          ...patch,
        },
      },
    });
  }

  function handleChange(e) {
    setDisplayTemperatureAsFahrenheit(e);
    stagePlayback({ displayTemperatureAsFahrenheit: e });
  }

  function handleParticleViewChange(e) {
    setDisplayParticlesAsLegacyCounts(e);
    stagePlayback({ displayParticlesAsLegacyCounts: e });
  }

  function handleSave() {
    if (!user) return;
    updateSettings(user.settings)
      .then(() => {
        infoNotification('Settings saved');
        refreshUser();
      })
      .catch((error) => {
        errorNotification(error.response?.data?.message || 'Failed to save settings');
      });
  }

  function onLogoutClick() {
    logout();
  }

  if (!user) {
    return null;
  }

  return (
    <>
      <Helmet>
        <title> VPSI | Account </title>
      </Helmet>
      {isLoading ? (
        <CircularProgress />
      ) : (
        <Container>
          <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
            <Typography variant="h4" gutterBottom>
              Account
            </Typography>
          </Stack>

          <Grid container spacing={1}>
            <Grid item xs={12} sm={12} md={12}>
              <Card>
                <CardHeader title="Information" />
                <CardContent>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    <strong>Username:</strong> {user.username}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    <strong>Email:</strong> {user.email}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={12} md={12}>
              <SubscriptionCard onManagePlan={() => navigate('/dashboard/plans')} />
            </Grid>
            <Grid item xs={12} sm={12} md={12}>
              <Card>
                <CardHeader title="Settings" />
                <CardContent>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Manage your account settings and set email preferences.
                  </Typography>
                  <FormControl sx={{ my: 2 }} focused={false}>
                    <FormLabel>Temperature Scale</FormLabel>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={displayTemperatureAsFahrenheit}
                          onChange={(e) => handleChange(e.target.checked)}
                        />
                      }
                      label="Display temperature in Fahrenheit"
                    />
                  </FormControl>
                  <FormControl sx={{ my: 2 }} focused={false} component="fieldset">
                    <FormLabel>Particle Readings</FormLabel>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={displayParticlesAsLegacyCounts}
                          onChange={(e) => handleParticleViewChange(e.target.checked)}
                        />
                      }
                      label="Show particle readings as derived counts, per 100 cm³"
                    />
                  </FormControl>
                  <br />
                  <br />
                  <br />
                  <Container disableGutters>
                    <Button variant="contained" color="primary" onClick={() => handleSave()}>
                      Save
                    </Button>
                  </Container>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={12} md={12}>
              <Card>
                <CardHeader title="Actions" />
                <CardContent>
                  <br />
                  <Button variant="contained" color="primary" fullWidth onClick={() => onLogoutClick()}>
                    Logout
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Container>
      )}
    </>
  );
}
